<template>
    <v-card v-if="exercise" flat tile :v-ripple="!readonly" class="pa-3 exercise-card"
        :class="{ 'exercise-card-compact': compact, 'student-edited': isEdited }">
        <div class="ex-row">
            <div @click="handleImageClick" class="ex-img-col"
                :class="{ 'compact-img-col': compact, 'img-col-disabled': imgBroken && noRedirect, 'img-clickable': imageClickable }">
                <div class="img-exercise" :style="{ backgroundImage: `url('${exerciseImg}')` }">
                    <div v-if="hasVideo && !compact" class="play-badge">
                        <v-icon size="13" color="white">mdi-play</v-icon>
                    </div>
                </div>
            </div>
            <div @click="handleExerciseClick" class="ex-body-col"
                :class="{ 'compact-body-col': compact }">
                <div class="ex-name-row">
                    <div class="name-duration-exercise text-truncate">
                        {{ exercise.name }}
                    </div>
                    <button-tooltip v-if="isEdited" variant="rich" tap-to-open :interactive="hasVideo">
                        <template v-slot:activator="{ on, attrs }">
                            <span class="ex-mod-dot" v-on="on" v-bind="attrs" />
                        </template>
                        <template v-slot:tooltip>
                            <exercise-change-boxes :changes="changes" @click:video="$emit('click:video', $event)" />
                        </template>
                    </button-tooltip>
                </div>
                <div :class="{ 'compact-prescription': compact }">
                    <template v-if="showCompleteSets && dataSet">
                        <div v-if="dataSet.repsType && dataSet.repsType.data">
                            <v-icon x-small>{{ dataSet.repsType.icon }}</v-icon>
                            <span class="duration-exercise" v-html="dataSet.repsType.data" />
                        </div>
                        <div v-if="dataSet.rpeType && dataSet.rpeType.data" class="duration-exercise">
                            <v-icon x-small>{{ dataSet.rpeType.icon }}</v-icon>
                            {{ dataSet.rpeType.data }}
                        </div>
                        <div v-if="dataSet.tempoType && dataSet.tempoType.data" class="duration-exercise">
                            <v-icon x-small>{{ dataSet.tempoType.icon }}</v-icon>
                            {{ dataSet.tempoType.data }}
                        </div>
                        <div v-if="dataSet.weight && dataSet.weight.data" class="duration-exercise">
                            <v-icon x-small>{{ dataSet.weight.icon }}</v-icon>
                            {{ dataSet.weight.data }}
                        </div>
                        <div v-if="dataSet.intensityType && dataSet.intensityType.data" class="duration-exercise">
                            <v-icon x-small>{{ dataSet.intensityType.icon }}</v-icon>
                            {{ dataSet.intensityType.data.replaceAll(':', '\'') }}
                        </div>
                        <div class="duration-exercise" :class="{ 'each-side-tag': compact }" v-if="exercise.eachSide">
                            {{ $t('exercises.each_side') }}
                        </div>
                    </template>
                    <div v-if="block.isCircuit" class="duration-exercise">
                        {{ exerciseSets }} {{ exercise.eachSide ? `| ${$t('exercises.each_side')}` : '' }}
                    </div>
                    <div v-else-if="showMetaLabel" class="duration-exercise">{{ metaLabel }}</div>
                </div>
                <div v-if="exercise.commentary" class="comment text-truncate">
                    {{ exercise.commentary }}
                </div>
            </div>
            <div v-if="withMenu && !readonly" class="ex-menu-col"
                :class="{ 'compact-menu-col': compact }">
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <div class="ex-menu-activator">
                            <v-btn icon small v-on="on" v-bind="attrs">
                                <v-icon>mdi-dots-horizontal</v-icon>
                            </v-btn>
                            <span v-if="painScale" class="pain-dot" />
                        </div>
                    </template>
                    <v-list dense>
                        <v-list-item v-for="option in options" :key="option.value" dense link
                            @click="onClickMenu(option)">
                            <v-list-item-title>
                                <v-icon left>{{ option.icon }}</v-icon>
                                {{ option.label }}
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
        </div>
    </v-card>
</template>

<script>

import { exerciseSetsLabel, setTypeIcon, SET_NAMES, findSetData } from '@/utils/Exercises'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import ExerciseChangeBoxes from '@/components/common/ExerciseChangeBoxes'

export default {
    components: { ButtonTooltip, ExerciseChangeBoxes },
    props: {
        exercise: {
            type: Object,
            default: null,
        },
        changes: {
            type: Array,
            default: null,
        },
        block: {
            type: Object,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false
        },
        showCompleteSets: {
            type: Boolean,
            default: false
        },
        noRedirect: {
            type: Boolean,
            default: false
        },
        withMenu: {
            type: Boolean,
            default: false
        },
        compact: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            dataSet: null,
            imgBroken: false,
        }
    },
    computed: {
        isEdited(vm = this) {
            return !!vm.changes?.length
        },
        hasVideo(vm = this) {
            return (vm.changes || []).some(change => change.type === 'video' && !change.expired)
        },
        assignedRoutineId(vm = this) {
            return vm.$route.params.uid
        },
        sets() {
            const vm = this
            return (vm.exercise?.config?.sets || []).filter(Boolean)
        },
        candidateImg() {
            const vm = this
            if (vm.exercise?.youtubeUrl) return vm.getYoutubeImgUrl(vm.exercise.youtubeUrl)
            return vm.exercise?.img || vm.exercise?.video?.thumb || vm.imgPlaceholder
        },
        exerciseImg() {
            const vm = this
            return vm.imgBroken ? vm.imgPlaceholder : vm.candidateImg
        },
        exerciseSets() {
            const vm = this
            if (vm.block?.type === 'tabata') return ''

            if (vm.block?.isCircuit) {
                let quantitySets = vm.block.sets
                let value = vm.exercise.config.value
                let type = vm.$t(`exercises.short_${vm.exercise.config.type}`)
                let note = vm.exercise.config.note ? `(${vm.exercise.config.note})` : ''
                if (vm.block.type === 'amrap' || vm.block.type === 'emom') return `${value}${note}${type}`
                return `${quantitySets}x${value}${note}${type}`
            }

            let config = vm.exercise.config || null
            if (!config || !config.sets?.length) return vm.$t('common.not_available_short')
            let quantitySets = config.sets.length
            let type = vm.$t(`exercises.${config.sets[0].data[0].short_name}`)

            let result = ''

            if (quantitySets === 1) {
                result = `${quantitySets}x${config.sets[0].data[0].value}${type}`
            } else {
                let values = []
                let current = null
                let sameValue = true
                config.sets.forEach(set => {
                    if (!current) current = set.data[0].value
                    values.push(set.data[0].value)
                    if (current !== set.data[0].value) sameValue = false
                    current = set.data[0].value
                })

                if (sameValue) {
                    result = `${quantitySets}x${values[0]}${type}`
                } else {
                    result = `${quantitySets}x${values.join(`${type},`)}${type}`
                }
            }
            return result
        },
        showMetaLabel(vm = this) {
            if (vm.compact || !vm.metaLabel) return false
            return !(vm.showCompleteSets && vm.dataSet)
        },
        hasIntensity(vm = this) {
            return !!vm.exercise?.categories?.find(category => vm.categoryKey(category) === vm.CATEGORY.SPEED || vm.categoryKey(category) === vm.CATEGORY.RESISTANCE)
        },
        options(vm = this) {
            let options = [
                { icon: 'mdi-timer-outline', value: 'timer', label: vm.$t('common.timer') },
                { icon: 'mdi-swap-horizontal', value: 'exercise', label: vm.$t('common.change_exercise') },
                { icon: 'mdi-camera', value: 'camera', label: vm.$t('common.camera') },
                { icon: 'mdi-debug-step-over', value: 'skip', label: vm.$t('common.skip') },
                { icon: 'mdi-history', value: 'history', label: vm.$t('common.history') },
            ]
            if (vm.painScale) options.push({ icon: '$painScale', value: 'pain', label: vm.$t('common.pain_scale') })
            return options
        },
        painScale(vm = this) {
            return vm.exercise?.painScale || false
        },
        hasVideo(vm = this) {
            return !!(vm.exercise?.youtubeUrl || vm.exercise?.video?.url)
        },
        imageClickable(vm = this) {
            if (vm.imgBroken && vm.noRedirect) return false
            if (vm.noRedirect) return vm.hasVideo
            return true
        },
        metaLabel(vm = this) {
            if (vm.block?.isCircuit) return ''
            const label = exerciseSetsLabel(vm.exercise, vm.block)
            if (!label) return ''
            return vm.exercise?.eachSide ? `${label} · ${vm.$t('exercises.each_side')}` : label
        },
    },
    mounted() {
        const vm = this
        vm.setData()
    },
    methods: {
        handleImageClick() {
            const vm = this
            if (vm.noRedirect) {
                if (vm.imgBroken) return;
                return vm.$emit('click:image', vm.exercise)
            }
            return vm.goToDetail()
        },
        handleExerciseClick() {
            const vm = this
            if (vm.noRedirect) return vm.$emit('click:config', vm.exercise)
            return vm.goToDetail()
        },
        goToDetail() {
            const vm = this
            return vm.$emit('click:showExercise')
        },
        setData() {
            const vm = this
            let data = {
                repsType: null,
                rpeType: null,
                tempoType: null,
                weight: null,
                intensityType: null,
                intensity: null,
                micropause: null,
            }
            vm.sets.forEach((set, i) => {
                if (i === 0) {
                    // INITIALIZE
                    let repsType = findSetData(set, vm.hasIntensity ? SET_NAMES.REPS_INTENSITY : SET_NAMES.REPS)
                    if (repsType) data.repsType = { data: '', icon: setTypeIcon(repsType.short_name), short_name: repsType.short_name }
                    let rpeType = vm.hasIntensity ? null : findSetData(set, SET_NAMES.RPE)
                    if (rpeType) data.rpeType = { data: '', icon: setTypeIcon(rpeType.short_name), short_name: rpeType.short_name }
                    let tempoType = findSetData(set, SET_NAMES.TEMPO)
                    if (tempoType) data.tempoType = { data: '', icon: setTypeIcon(tempoType.short_name), short_name: tempoType.short_name }
                    let weight = set.data && set.data.find(data => data.short_name === 'short_weight')
                    if (weight && !!weight.value) data.weight = {
                        data: '',
                        icon: setTypeIcon(weight.short_name),
                        short_name: weight.short_name
                    }
                    let intensityType = vm.hasIntensity ? findSetData(set, SET_NAMES.INTENSITY) : null
                    if (intensityType) data.intensityType = {
                        data: '',
                        icon: setTypeIcon(intensityType.short_name),
                        short_name: intensityType.short_name
                    }
                    let micropause = set.data && set.data.find(data => data.short_name === 'short_micropause')
                    if (micropause) data.micropause = {
                        data: '',
                        icon: setTypeIcon(micropause.short_name),
                        short_name: micropause.short_name
                    }
                }

                let separation = vm.sets.length > i + 1 ? '| ' : ''

                if (data.repsType) {
                    let repsEl = set.data && set.data.find(d => d.short_name === data.repsType.short_name)
                    if (repsEl) {
                        let valueReps = repsEl.value
                        if (data.micropause) {
                            let valueMp = ((set.data && set.data.find(d => d.short_name === data.micropause.short_name)?.value) ?? "") + ""
                            let stringReps = `${valueReps}${vm.$t(`exercises.${data.repsType.short_name}`)}`
                            let stringMp = valueMp === "0" || valueMp === "" || valueMp === "00:00" ? '' : `${valueMp}${vm.$t(`exercises.${data.micropause.short_name}`)}`
                            data.repsType.data += `${stringReps} <sup>${stringMp}</sup> ${separation}`
                        } else {
                            data.repsType.data += `${valueReps}${vm.$t(`exercises.${data.repsType.short_name}`)} ${separation}`
                        }
                    }
                }

                if (data.rpeType) {
                    let valueRpe = ((set.data && set.data.find(d => d.short_name === data.rpeType.short_name)?.value) ?? "0") + ""
                    if (valueRpe !== "0" && (data.rpeType.short_name === 'short_rpe' || data.rpeType.short_name === 'short_rir')) {
                        data.rpeType.data += `(${valueRpe}) ${separation}`
                    } else if (valueRpe !== "0") {
                        data.rpeType.data += `${valueRpe}${vm.$t(`exercises.${data.rpeType.short_name}`)} ${separation}`
                    }
                }

                if (data.tempoType) {
                    let valueTempo = ((set.data && set.data.find(d => d.short_name === data.tempoType.short_name)?.value) ?? "0") + ""
                    if (valueTempo !== "0") data.tempoType.data += `${valueTempo}${vm.$t(`exercises.${data.tempoType.short_name}`)} ${separation}`
                }

                if (data.weight) {
                    let weight = ((set.data && set.data.find(d => d.short_name === data.weight.short_name)?.value) ?? "0") + ""
                    if (weight !== "0") data.weight.data += `${weight}${vm.$t(`exercises.${data.weight.short_name}`)} ${separation}`
                }

                if (data.intensityType) {
                    let valueIntensity = ((set.data && set.data.find(d => d.short_name === data.intensityType.short_name)?.value) ?? "0") + ""
                    if (valueIntensity !== "0") data.intensityType.data += `${valueIntensity}${vm.$t(`exercises.${data.intensityType.short_name}`)} ${separation}`
                }

                if (data.micropause && data.repsType?.short_name !== 'short_reps') {
                    let valueMicropause = ((set.data && set.data.find(d => d.short_name === data.micropause.short_name)?.value) ?? "0") + ""
                    if (valueMicropause !== "0" && valueMicropause !== "" && valueMicropause !== "00:00") data.micropause.data += `${valueMicropause}${vm.$t(`exercises.${data.micropause.short_name}`)} ${separation}`
                }

            })

            vm.dataSet = data
        },
        onClickMenu(menu) {
            const vm = this;
            vm.$emit(`click:menu:${menu.value}`, vm.exercise)
        },
        checkImg(url) {
            const vm = this
            vm.imgBroken = false
            if (!url || url === vm.imgPlaceholder) return
            const img = new Image()
            img.onerror = () => { vm.imgBroken = true }
            img.src = url
        },
    },
    watch: {
        sets() {
            this.setData()
        },
        candidateImg: {
            immediate: true,
            handler(url) {
                this.checkImg(url)
            },
        },
    }
}
</script>

<style scoped>
.exercise-card:not(.exercise-card-compact) {
    background: var(--v-surface-base) !important;
    border: 1px solid var(--v-outline2-base);
    border-radius: 10px !important;
    min-height: 84px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.theme--dark .exercise-card:not(.exercise-card-compact) {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}

.ex-row {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    height: 100%;
}

.ex-img-col {
    flex: 0 0 auto;
    padding-right: 12px;
}

.ex-body-col {
    flex: 1 1 auto;
    min-width: 0;
}

.ex-menu-col {
    flex: 0 0 auto;
}

.ex-menu-activator {
    position: relative;
    display: inline-flex;
}

.pain-dot {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--v-primary-base);
    border: 1.5px solid var(--v-surface-base);
    pointer-events: none;
}

.ex-name-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.exercise-card.student-edited {
    background: rgba(232, 92, 92, .08) !important;
}

.ex-mod-dot {
    position: relative;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--v-error-base);
    box-shadow: 0 0 0 3px rgba(232, 92, 92, .18);
    flex-shrink: 0;
    cursor: pointer;
    animation: exModPulse 2s ease-in-out infinite;
}

.ex-mod-dot::after {
    content: '';
    position: absolute;
    inset: -12px;
}

@keyframes exModPulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: .45;
    }
}

.name-duration-exercise {
    font-size: 14px !important;
    line-height: 18px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.duration-exercise {
    font-size: 11px !important;
    line-height: 16px;
    font-weight: 600;
    letter-spacing: 0.03em;
    color: var(--v-accent-base);
    overflow-wrap: anywhere;
}

.theme--dark .duration-exercise {
    color: var(--v-primary-base);
}

.comment {
    display: inline-block;
    max-width: 100%;
    font-size: 11px;
    color: var(--v-txtMuted-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 6px;
    padding: 2px 8px;
    margin-top: 4px;
    cursor: pointer;
}

.img-exercise {
    position: relative;
    background-position: 50% 50%;
    background-size: cover;
    background-color: var(--v-surface2-base);
    height: 52px;
    width: 64px;
    border-radius: 8px;
    transition: filter 0.15s;
}

.img-clickable:hover .img-exercise {
    filter: brightness(0.6);
}

.play-badge {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.img-col-disabled {
    cursor: default;
}

.img-clickable {
    cursor: pointer;
}

/* ── Compact mode (Zapping columns) ── */
.exercise-card-compact {
    padding: 7px 10px !important;
    background: transparent !important;
}

.exercise-card-compact .compact-img-col {
    flex: 0 0 44px;
    padding: 0;
    display: flex;
    align-items: center;
}

.exercise-card-compact .compact-body-col {
    padding: 0 8px;
}

.exercise-card-compact .compact-menu-col {
    padding: 0;
}

.exercise-card-compact .img-exercise {
    width: 44px;
    height: 44px;
    border-radius: 6px;
}

.exercise-card-compact .img-clickable .img-exercise {
    position: relative;
    transition: filter 0.15s;
}

.exercise-card-compact .img-clickable:hover .img-exercise {
    filter: brightness(0.55);
}

.exercise-card-compact .img-clickable:hover .img-exercise::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 5px 0 5px 9px;
    border-color: transparent transparent transparent #fff;
}

.exercise-card-compact .name-duration-exercise {
    font-size: 11px !important;
    line-height: 15px;
    color: var(--v-txtPrimary-base);
    font-weight: 500;
}

/* Prescripción: todo en una línea, sin iconos */
.exercise-card-compact .compact-prescription {
    margin-top: 2px;
    line-height: 16px;
}

.exercise-card-compact .compact-prescription > div {
    display: inline;
    margin-right: 6px;
}

.exercise-card-compact .compact-prescription .v-icon {
    display: none;
}

.exercise-card-compact .duration-exercise {
    display: inline;
    font-size: 10px !important;
    color: var(--v-accent-base);
}

.exercise-card-compact .compact-prescription .each-side-tag {
    display: inline-block;
    font-size: 10px !important;
    font-weight: 500;
    color: var(--v-txtMuted-base);
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 4px;
    padding: 1px 6px;
    margin-right: 6px;
}

.exercise-card-compact .comment {
    color: var(--v-txtMuted-base);
    border-color: var(--v-outline-base);
    margin-top: 2px;
}
</style>